import type { DevServerLaunchPlan } from "@cmmd-forge/shared/devServerCommand";

import { isLoopbackPreviewUrl, localDevServerPreviewPort } from "~/lib/devServerPreviewUrl";

// Pure command-resolution helpers live in packages/shared so the
// `forge_dev_server_start` agent tool and this human "Launch app" button
// share one implementation and cannot drift. Re-exported here so existing
// imports of this module keep working unchanged.
export {
  BROWSER_PREVIEW_TERMINAL_ID,
  buildPortAwareDevServerCommand,
  pickDevServerScript,
  resolveDevServerLaunchPlan,
  scoreDevServerScript,
  type DevServerLaunchPlan,
} from "@cmmd-forge/shared/devServerCommand";

export interface BrowserDevServerLaunchCardVm {
  readonly title: string;
  readonly description: string;
  readonly terminalSummary: string;
  readonly actionLabel: string;
}

export interface RunningDevServerPreviewLaunch {
  readonly allocatedPort: number;
  readonly displayPreviewUrl: string;
}

export function shouldReleaseDevRuntimeBeforeLaunch(input: {
  readonly runningTerminalCount: number;
}): boolean {
  return input.runningTerminalCount === 0;
}

export function shouldRunDevServerLaunch(input: {
  readonly launchBusy: boolean;
  readonly hasProject: boolean;
  readonly hasBrowserThreadContext: boolean;
  readonly hasEnvironmentApi: boolean;
}): boolean {
  return (
    !input.launchBusy &&
    input.hasProject &&
    input.hasBrowserThreadContext &&
    input.hasEnvironmentApi
  );
}

export function resolveRunningDevServerPreviewLaunch(input: {
  readonly runningTerminalCount: number;
  readonly latestThreadDevServerPreviewUrl: string | null;
}): RunningDevServerPreviewLaunch | null {
  if (input.runningTerminalCount <= 0 || !input.latestThreadDevServerPreviewUrl) {
    return null;
  }

  const allocatedPort = localDevServerPreviewPort(input.latestThreadDevServerPreviewUrl);
  if (!allocatedPort) {
    return null;
  }

  return {
    allocatedPort,
    displayPreviewUrl: `http://localhost:${allocatedPort}/`,
  };
}

export function shouldShowDevServerLaunchCard(input: {
  readonly activeTabDisplayUrl: string | null;
  readonly hasHealthyActiveBrowserTab: boolean;
  readonly hasProject: boolean;
  readonly hasThread: boolean;
  readonly devServerLaunchPlanId: string;
  readonly devServerLaunchPlanSource: "project-script" | "heuristic";
  readonly dismissedDevHintScriptId: string | null;
  readonly hasVisiblePreview: boolean;
  readonly launchedDevScriptId: string | null;
  readonly runningTerminalCount: number;
}): boolean {
  if (!input.hasProject || !input.hasThread) {
    return false;
  }
  if (input.devServerLaunchPlanId === input.dismissedDevHintScriptId) {
    return false;
  }
  if (input.hasVisiblePreview) {
    return false;
  }
  if (
    input.hasHealthyActiveBrowserTab &&
    input.launchedDevScriptId !== input.devServerLaunchPlanId
  ) {
    return false;
  }
  if (input.activeTabDisplayUrl && !isLoopbackPreviewUrl(input.activeTabDisplayUrl)) {
    return false;
  }
  if (input.devServerLaunchPlanSource === "project-script") {
    return true;
  }
  if (!input.activeTabDisplayUrl) {
    return false;
  }
  return true;
}

export function resolveDevServerRelaunchTargetUrl(input: {
  readonly activeTabDisplayUrl: string | null;
  readonly recoverableDevPreviewUrl: string | null;
  readonly addressLocalPreviewUrl: string | null;
  readonly latestThreadDevServerPreviewUrl: string | null;
}): string | null {
  return (
    input.activeTabDisplayUrl ??
    input.recoverableDevPreviewUrl ??
    input.addressLocalPreviewUrl ??
    input.latestThreadDevServerPreviewUrl
  );
}

export function resolveBrowserDevServerLaunchCardVm(input: {
  readonly devServerLaunchPlan: Pick<DevServerLaunchPlan, "source" | "name" | "command" | "id">;
  readonly runningTerminalCount: number;
  readonly terminalCount: number;
  readonly latestThreadDevServerPreviewUrl: string | null;
  readonly launchedDevScriptId: string | null;
}): BrowserDevServerLaunchCardVm {
  const source = input.devServerLaunchPlan.source;
  const runningTerminalLabel =
    input.runningTerminalCount > 0
      ? `${input.runningTerminalCount} running terminal${
          input.runningTerminalCount === 1 ? "" : "s"
        }`
      : "No running terminals";

  return {
    title: source === "project-script" ? "App server detected" : "App command suggested",
    description:
      source === "heuristic"
        ? "Forge will auto-detect a dev command and open it in Browser."
        : `${input.devServerLaunchPlan.name}: ${input.devServerLaunchPlan.command}`,
    terminalSummary: `${runningTerminalLabel} · ${input.terminalCount} total`,
    actionLabel:
      input.runningTerminalCount > 0 && input.latestThreadDevServerPreviewUrl
        ? "Open app"
        : input.launchedDevScriptId === input.devServerLaunchPlan.id
          ? "Launch again"
          : "Launch app",
  };
}
